import { decamelizeKeys } from 'humps';

import { apiClient } from '../apiClient';

export const PATHS = {
  // Catalog of all selectable goals.
  HEALTH_GOALS: '/api/v3/health-goals/',
  // The user's own selected goals — read + write, both keyed by `?user_urn=`.
  // Mirrors Sparta's `user/api/healthGoals` (sparta/src/user/api/healthGoals.ts).
  USER_HEALTH_GOALS: '/api/v3/user-health-goals/',
} as const;

// One goal in the catalog. Wire shape: `{ urn, key, name, icon }` — `urn` is
// the id (Sparta's idAttribute), `name` the display label, and `icon` the
// avatar image URL for the card.
export type HealthGoal = {
  urn: string;
  key: string;
  name: string;
  icon?: string;
};

type HealthGoalsResponse = {
  items: HealthGoal[];
};

// The user's saved goals come back as items keyed by `urn`; only `urn` is
// needed to seed the form selection.
type UserHealthGoalsResponse = {
  items: { urn: string }[];
};

export async function fetchHealthGoals(signal?: AbortSignal): Promise<HealthGoal[]> {
  const res = await apiClient.get<HealthGoalsResponse>(PATHS.HEALTH_GOALS, { signal });
  return res.data?.items ?? [];
}

// The user's currently-selected goal urns (for prefill).
export async function fetchUserHealthGoals(
  userUrn: string,
  signal?: AbortSignal
): Promise<string[]> {
  const res = await apiClient.get<UserHealthGoalsResponse>(PATHS.USER_HEALTH_GOALS, {
    params: { user_urn: userUrn },
    signal,
  });
  return (res.data?.items ?? []).map((item) => item.urn);
}

// Persist the user's selection. Mirrors Sparta's patchUserHealthGoals: PATCH
// with `{ urns }`, `user_urn` in the query string.
export async function updateUserHealthGoals(userUrn: string, urns: string[]): Promise<void> {
  await apiClient.patch(PATHS.USER_HEALTH_GOALS, decamelizeKeys({ urns }), {
    params: { user_urn: userUrn },
  });
}
